import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProblemError, useServices } from '@/model';
import { Button, Icon } from '@/ui';
import { Footnote, Group, Notice } from '../components';
import { useAction, useScope } from '../resources/hooks';
import { fetchMe } from '../resources/api';
import type { LegalStatus } from '../resources/types';
import { safeWebUrl } from '../domain';

export function LegalReviewSheet({ legal, onClose, onShown }: { legal: LegalStatus; onClose: () => void; onShown?: (pair: string) => void }) {
  const { t, i18n } = useTranslation('me'); const services = useServices(); const scope = useScope(); const op = useAction();
  const [shown, setShown] = useState(legal); const [changed, setChanged] = useState(false);
  useEffect(() => {
    const pair = shown.terms_version + '|' + shown.privacy_version;
    if (!scope.valid()) return;
    onShown?.(pair);
    try { localStorage.setItem('hylo.web.legal.seen.' + scope.accountId, pair); } catch { /* The runtime retains its in-memory seen set. */ }
  }, [scope, shown.terms_version, shown.privacy_version, onShown]);
  return <div className="me-sheet"><h2>{t('legal.updated')}</h2><Footnote>{t('legal.help')}</Footnote><h3 className="me-section-title">{t('legal.review')}</h3>
    <Group>{(['terms', 'privacy'] as const).map(document => {
      const url = safeWebUrl(shown[document === 'terms' ? 'terms_url' : 'privacy_url'], i18n.resolvedLanguage?.startsWith('zh') ? 'zh-Hans' : 'en');
      return url && <a className="me-external" key={document} href={url} target="_blank" rel="noopener noreferrer">
        <Icon name={document === 'terms' ? 'doc.text.fill' : 'hand.raised.fill'} /><span>{t('about.' + document)}<small className="me-footnote">{t('about.version', { version: shown[document === 'terms' ? 'terms_version' : 'privacy_version'] })}</small></span><Icon name="arrow.up.right" /></a>;
    })}</Group>
    {changed && <Footnote>{t('legal.changedHelp')}</Footnote>}<Notice error={op.error} />
    <div className="me-actions"><Button disabled={op.busy} onClick={onClose}>{t('common:close')}</Button><Button variant="prominent" loading={op.busy}
      onClick={() => { const ticket = scope.generation; const pair = { terms_version: shown.terms_version, privacy_version: shown.privacy_version };
        void op.run(async () => {
          try { await services.http.post('/v1/me/consents', pair, { signal: scope.abort.signal }); }
          catch (e) {
            if (e instanceof ProblemError && e.code === 'consent_version_mismatch') {
              const fresh = await fetchMe(services, scope.abort.signal); scope.check(ticket);
              if (fresh.legal) { setShown(fresh.legal); setChanged(true); }
            } throw e;
          }
        }, false).then(ok => { if (ok) onClose(); });
      }}>{t('actions.continue')}</Button></div>
  </div>;
}

